import React, { useEffect, useState, useCallback } from 'react';
import { Link, useParams } from 'react-router-dom';
import axios from 'axios';
import AdminLayout from '../../components/AdminLayout';
import { describeLine } from '../../lib/cartLine';
import {
  API, withCreds, inputCls, smallInputCls, selectCls, btnPrimary, btnSecondary, btnDanger, ORDER_STATUSES,
  Field, Modal, Spinner, StatusBadge, money, fmtDateTime, errMsg,
} from '../../components/admin/AdminUI';
import {
  ArrowLeft, Printer, Truck, Package, User, MapPin, MessageSquare, History, RotateCcw, XCircle,
  ExternalLink, Trash2, AlertTriangle, Edit, Check,
} from 'lucide-react';
import Select from '../../components/FlatSelect';

// ── Cancel / refund ───────────────────────────────────────────────
function RefundCancelModal({ order, onClose, onDone }) {
  const [refundType, setRefundType] = useState('cancel');
  const [notes, setNotes] = useState('');
  const [restock, setRestock] = useState(true);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const hasSquare = !!order.square_payment_id;

  const submit = async () => {
    setLoading(true);
    try {
      const r = await axios.post(`${API}/admin/orders/${order.id}/refund`, { refund_type: refundType, notes, restock }, withCreds);
      setResult(r.data);
      onDone();
    } catch (e) { setResult({ error: errMsg(e, 'Action failed') }); }
    finally { setLoading(false); }
  };

  return (
    <Modal title="Cancel / Refund Order" onClose={onClose} width="max-w-md">
      {result ? (
        <div className="space-y-2">
          {result.error ? <p className="text-red-600 text-sm">{result.error}</p> : (
            <>
              <p className="text-green-600 font-bold">{result.message}</p>
              {result.square_result === 'success' && <p className="text-honey-700 text-sm">Square refund processed.</p>}
              {result.square_error && <p className="text-amber-600 text-sm">Square refund note: {result.square_error}. Status was updated anyway.</p>}
            </>
          )}
          <button onClick={onClose} className={`${btnSecondary} w-full mt-3`}>Close</button>
        </div>
      ) : (
        <div className="space-y-4">
          <p className="text-ink/70 text-sm">Order <b className="text-ink">{order.order_number}</b> · Total <b className="text-honey-700">{money(order.total)}</b></p>
          <div className="flex gap-3">
            <button onClick={() => setRefundType('cancel')} data-testid="action-cancel-btn"
              className={`flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl border text-sm font-bold ${refundType === 'cancel' ? 'bg-red-500/10 border-red-500/30 text-red-600' : 'bg-ink/[0.04] border-ink/10 text-ink/60'}`}>
              <XCircle className="w-4 h-4" /> Cancel only
            </button>
            <button onClick={() => setRefundType('refund')} data-testid="action-refund-btn"
              className={`flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl border text-sm font-bold ${refundType === 'refund' ? 'bg-orange-500/10 border-orange-500/30 text-orange-600' : 'bg-ink/[0.04] border-ink/10 text-ink/60'}`}>
              <RotateCcw className="w-4 h-4" /> Refund
            </button>
          </div>
          {refundType === 'refund' && (
            <p className="text-xs text-ink/45">{hasSquare ? 'The Square payment will be refunded automatically.' : 'No Square payment on this order. Only the status will change, so refund the customer yourself.'}</p>
          )}
          <label className="flex items-center gap-2 cursor-pointer">
            <input type="checkbox" checked={restock} onChange={e => setRestock(e.target.checked)} className="accent-honey" />
            <span className="text-ink/75 text-sm">Return items to stock</span>
          </label>
          <Field label="Internal notes (optional)">
            <textarea value={notes} onChange={e => setNotes(e.target.value)} rows={2} className={`${inputCls} resize-none`} />
          </Field>
          <button onClick={submit} disabled={loading} data-testid="confirm-refund-btn"
            className={`w-full py-2.5 rounded-xl font-bold text-sm text-ink disabled:opacity-50 ${refundType === 'refund' ? 'bg-orange-500 hover:bg-orange-600' : 'bg-red-500 hover:bg-red-600'}`}>
            {loading ? 'Processing…' : refundType === 'refund' ? 'Process refund' : 'Cancel order'}
          </button>
        </div>
      )}
    </Modal>
  );
}

// ── Shipping labels ───────────────────────────────────────────────
function ShippingPanel({ order, onChanged }) {
  const [status, setStatus] = useState(null);
  const [parcel, setParcel] = useState({ length: '', width: '', height: '', weight_oz: order.weight_oz || '' });
  const [rates, setRates] = useState(null);
  const [usedParcel, setUsedParcel] = useState(null);
  const [rateId, setRateId] = useState('');
  const [notify, setNotify] = useState(true);
  const [busy, setBusy] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    axios.get(`${API}/admin/shipping/status`, withCreds).then(r => setStatus(r.data)).catch(() => setStatus({ configured: false }));
    axios.get(`${API}/admin/store-settings`, withCreds).then(r => {
      const d = r.data.default_parcel || {};
      setParcel(p => ({ length: p.length || d.length, width: p.width || d.width, height: p.height || d.height, weight_oz: p.weight_oz }));
    }).catch(() => {});
  }, []);

  const getRates = async () => {
    setBusy('rates'); setError(''); setRates(null); setRateId('');
    try {
      const r = await axios.post(`${API}/admin/orders/${order.id}/shipping/rates`, parcel, withCreds);
      setRates(r.data.rates);
      setUsedParcel(r.data.parcel);
      setParcel(p => ({ ...p, weight_oz: r.data.parcel.weight_oz }));
      setRateId(r.data.rates[0]?.rate_id || '');
    } catch (e) { setError(errMsg(e, 'Could not get rates')); }
    finally { setBusy(''); }
  };

  const buy = async () => {
    const rate = rates.find(r => r.rate_id === rateId);
    if (!rate || !window.confirm(`Buy ${rate.carrier} ${rate.service} label for ${money(rate.amount)}?`)) return;
    setBusy('buy'); setError('');
    try {
      await axios.post(`${API}/admin/orders/${order.id}/shipping/label`, { rate, parcel: usedParcel, notify }, withCreds);
      setRates(null);
      onChanged();
    } catch (e) { setError(errMsg(e, 'Label purchase failed')); }
    finally { setBusy(''); }
  };

  const voidLabel = async (s) => {
    if (!window.confirm(`Void the ${s.carrier} label ${s.tracking_number}? Shippo will request a refund for the unused label.`)) return;
    setBusy('void'); setError('');
    try {
      await axios.post(`${API}/admin/orders/${order.id}/shipping/void`, { transaction_id: s.transaction_id }, withCreds);
      onChanged();
    } catch (e) { setError(errMsg(e, 'Void failed')); }
    finally { setBusy(''); }
  };

  const shipments = order.shipments || [];
  const canShip = !['cancelled', 'refunded'].includes(order.status);

  return (
    <div className="admin-card rounded-xl p-5">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-ink font-bold flex items-center gap-2"><Truck className="w-4 h-4 text-honey-700" /> Shipping</h2>
        {status?.test_mode && <span className="text-xs px-2 py-0.5 rounded-full bg-yellow-500/10 text-amber-600 border border-yellow-500/20">Shippo test mode</span>}
      </div>

      {shipments.length > 0 && (
        <div className="space-y-2 mb-4">
          {shipments.map(s => (
            <div key={s.transaction_id} className={`flex flex-wrap items-center gap-3 p-3 rounded-xl border ${s.status === 'voided' ? 'border-ink/5 opacity-50' : 'border-ink/10 bg-ink/[0.04]'}`}>
              <div className="flex-1 min-w-[180px]">
                <p className="text-ink text-sm font-medium">{s.carrier} · {s.service} <span className="text-ink/55">({money(s.cost)})</span></p>
                <p className="text-ink/55 text-xs font-mono">{s.tracking_number}{s.status === 'voided' ? ` · voided (${(s.refund_status || '').toLowerCase()})` : ''}</p>
              </div>
              {s.status !== 'voided' && (
                <>
                  <a href={s.label_url} target="_blank" rel="noreferrer" className={btnPrimary}><Printer className="w-4 h-4" /> Print label</a>
                  {s.tracking_url && <a href={s.tracking_url} target="_blank" rel="noreferrer" className={btnSecondary}><ExternalLink className="w-4 h-4" /> Track</a>}
                  <button onClick={() => voidLabel(s)} disabled={!!busy} className="text-ink/45 hover:text-red-600 text-xs">Void</button>
                </>
              )}
            </div>
          ))}
        </div>
      )}
      {order.tracking_status && <p className="text-ink/60 text-xs mb-4">Latest carrier status: <b className="text-ink/85">{order.tracking_status.replace('_', ' ').toLowerCase()}</b>{order.tracking_status_detail ? ` · ${order.tracking_status_detail}` : ''}</p>}

      {!status ? <Spinner /> : !status.configured ? (
        <p className="text-ink/55 text-sm">To buy labels here, add <code className="text-honey-700">SHIPPO_API_KEY</code> to the backend's environment variables in Coolify. Until then, enter tracking numbers by hand on the right.</p>
      ) : !status.ship_from_complete ? (
        <p className="text-ink/55 text-sm">Add your ship-from address in <Link to="/admin/settings" className="text-honey-700 underline">Settings</Link> to start buying labels.</p>
      ) : canShip && (
        <div className="space-y-3">
          <div className="grid grid-cols-4 gap-2">
            {[['length', 'L (in)'], ['width', 'W (in)'], ['height', 'H (in)'], ['weight_oz', 'Weight (oz)']].map(([k, l]) => (
              <Field key={k} label={l}>
                <input type="number" step="0.1" min="0" value={parcel[k] ?? ''} onChange={e => setParcel(p => ({ ...p, [k]: e.target.value }))} className={`${smallInputCls} w-full`} />
              </Field>
            ))}
          </div>
          <p className="text-ink/45 text-xs">Weight defaults to the total of the product weights on this order. Change the box size to match what you're shipping in.</p>
          <button onClick={getRates} disabled={!!busy} className={btnSecondary}>{busy === 'rates' ? 'Getting rates…' : shipments.some(s => s.status === 'active') ? 'Get rates for another label' : 'Get shipping rates'}</button>

          {rates && (
            <div className="space-y-2">
              <div className="max-h-72 overflow-y-auto space-y-1">
                {rates.map(r => (
                  <label key={r.rate_id} className={`flex items-center gap-3 p-3 rounded-xl border cursor-pointer ${rateId === r.rate_id ? 'border-honey/50 bg-honey/5' : 'border-ink/10 hover:bg-ink/[0.04]'}`}>
                    <input type="radio" name="rate" checked={rateId === r.rate_id} onChange={() => setRateId(r.rate_id)} className="accent-honey" />
                    <div className="flex-1">
                      <p className="text-ink text-sm">{r.carrier} · {r.service}</p>
                      <p className="text-ink/55 text-xs">{r.estimated_days ? `${r.estimated_days} day(s)` : r.duration_terms}</p>
                    </div>
                    <span className="text-ink font-bold">{money(r.amount)}</span>
                  </label>
                ))}
              </div>
              <label className="flex items-center gap-2 text-ink/70 text-sm cursor-pointer">
                <input type="checkbox" checked={notify} onChange={e => setNotify(e.target.checked)} className="accent-honey" /> Email the customer their tracking number
              </label>
              <button onClick={buy} disabled={!rateId || !!busy} className={`${btnPrimary} w-full`}>
                {busy === 'buy' ? 'Buying label…' : `Buy label${rateId ? ` · ${money(rates.find(r => r.rate_id === rateId)?.amount)}` : ''}`}
              </button>
            </div>
          )}
        </div>
      )}
      {error && <p className="text-red-600 text-sm p-3 bg-red-500/10 rounded-xl mt-3 break-words">{error}</p>}
    </div>
  );
}

// ── Address editor ────────────────────────────────────────────────
function AddressCard({ order, onSave }) {
  const [editing, setEditing] = useState(false);
  const [addr, setAddr] = useState(order.shipping_address || {});
  const a = order.shipping_address || {};
  const fields = [['name', 'Name'], ['line1', 'Street'], ['line2', 'Apt / suite'], ['city', 'City'], ['state', 'State'], ['postal_code', 'ZIP'], ['country', 'Country'], ['phone', 'Phone']];

  return (
    <div className="admin-card rounded-xl p-5">
      <div className="flex items-center justify-between mb-3">
        <h2 className="text-ink font-bold flex items-center gap-2"><MapPin className="w-4 h-4 text-honey-700" /> Ship to</h2>
        {!editing && <button onClick={() => { setAddr(a); setEditing(true); }} className="text-ink/55 hover:text-honey-700"><Edit className="w-4 h-4" /></button>}
      </div>
      {editing ? (
        <div className="space-y-2">
          {fields.map(([k, l]) => (
            <input key={k} value={addr[k] || ''} onChange={e => setAddr({ ...addr, [k]: e.target.value })} placeholder={l} className={`${smallInputCls} w-full`} />
          ))}
          <div className="flex gap-2">
            <button onClick={async () => { await onSave({ shipping_address: addr }); setEditing(false); }} className={btnPrimary}><Check className="w-4 h-4" /> Save</button>
            <button onClick={() => setEditing(false)} className={btnSecondary}>Cancel</button>
          </div>
        </div>
      ) : (
        <p className="text-ink/75 text-sm leading-relaxed">
          {a.name}<br />{a.line1}{a.line2 ? <><br />{a.line2}</> : null}<br />
          {a.city}, {a.state} {a.postal_code}<br />{a.country}
          {a.phone && <><br /><span className="text-ink/55">{a.phone}</span></>}
        </p>
      )}
    </div>
  );
}

// ── Page ──────────────────────────────────────────────────────────
export default function AdminOrderDetail() {
  const { id } = useParams();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [newStatus, setNewStatus] = useState('');
  const [statusNote, setStatusNote] = useState('');
  const [notify, setNotify] = useState(true);
  const [tracking, setTracking] = useState({ tracking_number: '', carrier: '', tracking_url: '' });
  const [note, setNote] = useState('');
  const [showRefund, setShowRefund] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/admin/orders/${id}`, withCreds);
      setOrder(r.data);
      setNewStatus(r.data.status);
      setTracking({ tracking_number: r.data.tracking_number || '', carrier: r.data.carrier || '', tracking_url: r.data.tracking_url || '' });
    } finally { setLoading(false); }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  const update = async (body) => {
    setSaving(true);
    try {
      const r = await axios.put(`${API}/admin/orders/${id}`, body, withCreds);
      await load();
      return r.data;
    } catch (e) { alert(errMsg(e)); }
    finally { setSaving(false); }
  };

  const saveStatus = async () => {
    if (['cancelled', 'refunded'].includes(newStatus)) { setShowRefund(true); return; }
    await update({ status: newStatus, status_note: statusNote, notify });
    setStatusNote('');
  };

  const addNote = async () => {
    if (!note.trim()) return;
    await axios.post(`${API}/admin/orders/${id}/notes`, { text: note }, withCreds);
    setNote('');
    load();
  };

  const deleteNote = async (noteId) => {
    if (!window.confirm('Delete this note?')) return;
    await axios.delete(`${API}/admin/orders/${id}/notes/${noteId}`, withCreds);
    load();
  };

  if (loading) return <AdminLayout><Spinner /></AdminLayout>;
  if (!order) return <AdminLayout><p className="text-ink/55">Order not found.</p></AdminLayout>;

  const closed = ['cancelled', 'refunded'].includes(order.status);
  const notes = [...(order.internal_notes || [])].reverse();
  if (order.notes) notes.push({ id: 'legacy', text: order.notes, at: null, by: '' });

  return (
    <AdminLayout>
      {showRefund && <RefundCancelModal order={order} onClose={() => { setShowRefund(false); setNewStatus(order.status); }} onDone={load} />}

      <Link to="/admin/orders" className="inline-flex items-center gap-1 text-ink/55 hover:text-ink text-sm mb-4"><ArrowLeft className="w-4 h-4" /> Orders</Link>
      <div className="flex flex-wrap items-center gap-3 mb-6">
        <h1 className="text-2xl font-black text-ink font-mono">{order.order_number}</h1>
        <StatusBadge status={order.status} />
        <span className="text-ink/45 text-sm">{fmtDateTime(order.created_at)}</span>
        <div className="flex gap-2 ml-auto">
          <a href={`/admin/orders/${order.id}/packing-slip`} target="_blank" rel="noreferrer" className={btnSecondary}><Printer className="w-4 h-4" /> Packing slip</a>
          {!closed && <button onClick={() => setShowRefund(true)} className={btnDanger} data-testid="refund-cancel-btn">Cancel / Refund</button>}
        </div>
      </div>
      {order.refund_error && <p className="text-amber-600 text-sm p-3 bg-yellow-500/10 rounded-xl mb-4 flex items-center gap-2"><AlertTriangle className="w-4 h-4" /> Last refund attempt: {order.refund_error}</p>}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          {/* Items */}
          <div className="admin-card rounded-xl p-5">
            <h2 className="text-ink font-bold flex items-center gap-2 mb-4"><Package className="w-4 h-4 text-honey-700" /> Items</h2>
            <div className="divide-y divide-ink/5">
              {order.items?.map((item, i) => (
                <div key={i} className="flex items-center gap-3 py-2">
                  <div className="w-12 h-12 bg-ink/[0.04] rounded-lg overflow-hidden flex-shrink-0">
                    {item.image && <img src={item.image} alt="" className="w-full h-full object-cover" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-ink text-sm truncate">{item.title}</p>
                    {describeLine(item) && <p className="text-honey-600/80 text-xs">{describeLine(item)}</p>}
                    <p className="text-ink/55 text-xs">{item.sku && <span className="font-mono mr-2">{item.sku}</span>}{money(item.price)} × {item.quantity}</p>
                  </div>
                  <p className="text-ink font-bold text-sm">{money(item.price * item.quantity)}</p>
                </div>
              ))}
            </div>
            <div className="border-t border-ink/10 mt-3 pt-3 space-y-1 text-sm max-w-xs ml-auto">
              <div className="flex justify-between text-ink/60"><span>Subtotal</span><span>{money(order.subtotal)}</span></div>
              {order.discounts?.length ? order.discounts.map((d, i) => (
                <div key={i} className="flex justify-between text-green-600"><span>{d.name}{d.sets > 1 ? ` ×${d.sets}` : ''}</span><span>−{money(d.amount)}</span></div>
              )) : order.discount > 0 && <div className="flex justify-between text-green-600"><span>Discount{order.coupon_code ? ` (${order.coupon_code})` : ''}</span><span>−{money(order.discount)}</span></div>}
              <div className="flex justify-between text-ink/60"><span>Shipping{order.weight_oz ? ` (${order.weight_oz} oz)` : ''}</span><span>{money(order.shipping)}</span></div>
              <div className="flex justify-between text-ink/60"><span>Tax</span><span>{money(order.tax)}</span></div>
              <div className="flex justify-between text-ink font-bold pt-1 border-t border-ink/10"><span>Total</span><span className="text-honey-700">{money(order.total)}</span></div>
              <p className="text-ink/45 text-xs text-right">{order.square_payment_id ? `Paid by card (Square) · ${order.payment_status}` : `Payment: ${order.payment_status || 'not collected'}`}</p>
            </div>
          </div>

          <ShippingPanel order={order} onChanged={load} />

          {/* Notes */}
          <div className="admin-card rounded-xl p-5">
            <h2 className="text-ink font-bold flex items-center gap-2 mb-3"><MessageSquare className="w-4 h-4 text-honey-700" /> Internal notes</h2>
            <div className="flex gap-2 mb-3">
              <input value={note} onChange={e => setNote(e.target.value)} onKeyDown={e => e.key === 'Enter' && addNote()} placeholder="Add a note (only staff can see this)…" className={inputCls} />
              <button onClick={addNote} disabled={!note.trim()} className={btnPrimary}>Add</button>
            </div>
            <div className="space-y-2">
              {notes.length === 0 && <p className="text-ink/45 text-sm">No notes yet.</p>}
              {notes.map(n => (
                <div key={n.id} className="group p-3 bg-ink/[0.04] rounded-xl">
                  <p className="text-ink/85 text-sm whitespace-pre-wrap">{n.text}</p>
                  <div className="flex items-center justify-between mt-1">
                    <p className="text-ink/45 text-xs">{n.at ? `${n.by} · ${fmtDateTime(n.at)}` : 'Earlier note'}</p>
                    {n.id !== 'legacy' && <button onClick={() => deleteNote(n.id)} className="opacity-0 group-hover:opacity-100 text-ink/45 hover:text-red-600"><Trash2 className="w-3 h-3" /></button>}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="space-y-6">
          {/* Customer */}
          <div className="admin-card rounded-xl p-5">
            <h2 className="text-ink font-bold flex items-center gap-2 mb-3"><User className="w-4 h-4 text-honey-700" /> Customer</h2>
            <p className="text-ink text-sm">{order.customer_name}</p>
            <a href={`mailto:${order.customer_email}`} className="text-honey-700 text-sm hover:underline break-all">{order.customer_email}</a>
            <p className="text-ink/55 text-xs mt-2">{order.customer_order_count} order{order.customer_order_count === 1 ? '' : 's'} total</p>
            {order.customer_id && <Link to={`/admin/customers/${order.customer_id}`} className="text-ink/60 hover:text-ink text-xs underline">View customer</Link>}
          </div>

          <AddressCard order={order} onSave={update} />

          {/* Status */}
          <div className="admin-card rounded-xl p-5 space-y-3">
            <h2 className="text-ink font-bold">Status</h2>
            <Select value={newStatus} onChange={e => setNewStatus(e.target.value)} data-testid="status-select" className={`${selectCls} w-full capitalize`}>
              {ORDER_STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
            </Select>
            {newStatus !== order.status && (
              <>
                <input value={statusNote} onChange={e => setStatusNote(e.target.value)} placeholder="Note for the history (optional)" className={`${smallInputCls} w-full`} />
                {['shipped', 'delivered'].includes(newStatus) && (
                  <label className="flex items-center gap-2 text-ink/70 text-sm cursor-pointer">
                    <input type="checkbox" checked={notify} onChange={e => setNotify(e.target.checked)} className="accent-honey" /> Email the customer
                  </label>
                )}
                <button onClick={saveStatus} disabled={saving} className={`${btnPrimary} w-full`}>Update status</button>
              </>
            )}
          </div>

          {/* Manual tracking */}
          <div className="admin-card rounded-xl p-5 space-y-2">
            <h2 className="text-ink font-bold">Tracking</h2>
            <p className="text-ink/45 text-xs">Filled in automatically when you buy a label. Enter it here if you shipped another way.</p>
            <input value={tracking.carrier} onChange={e => setTracking({ ...tracking, carrier: e.target.value })} placeholder="Carrier (e.g. USPS)" className={`${smallInputCls} w-full`} />
            <input value={tracking.tracking_number} onChange={e => setTracking({ ...tracking, tracking_number: e.target.value })} placeholder="Tracking number" data-testid="tracking-input" className={`${smallInputCls} w-full font-mono`} />
            <input value={tracking.tracking_url} onChange={e => setTracking({ ...tracking, tracking_url: e.target.value })} placeholder="Tracking link (optional)" className={`${smallInputCls} w-full`} />
            <button onClick={() => update(tracking)} disabled={saving} className={`${btnSecondary} w-full`}>Save tracking</button>
          </div>

          {/* History */}
          <div className="admin-card rounded-xl p-5">
            <h2 className="text-ink font-bold flex items-center gap-2 mb-3"><History className="w-4 h-4 text-honey-700" /> History</h2>
            <ol className="space-y-3 border-l border-ink/10 ml-1">
              {[...(order.status_history || [])].reverse().map((h, i) => (
                <li key={i} className="pl-4 relative">
                  <span className="absolute -left-1 top-1.5 w-2 h-2 rounded-full bg-honey" />
                  <p className="text-ink/85 text-sm capitalize">{h.status}{h.note ? <span className="normal-case text-ink/55"> · {h.note}</span> : null}</p>
                  <p className="text-ink/45 text-xs">{fmtDateTime(h.at)} · {h.by}</p>
                </li>
              ))}
              {!order.status_history?.length && <li className="pl-4 text-ink/45 text-sm">No history recorded for this order.</li>}
            </ol>
          </div>
        </div>
      </div>
    </AdminLayout>
  );
}
